import { autoinject, transient } from "aurelia-dependency-injection";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { Messages } from "../../../configuration/event-messages.constants";
import { AnonVesselDetailModel } from "../../../models/vessels/vessel.models";
import { GhgVerificationModel, SaveGhgVerificationResponse, GhgVerificationCorrespondenceEmail, GhgVerificationStatusEnum } from "../../../models/ghgs/ghg.models";
import { NotificationType, NotificationClientService } from "../../../services/basics/notification-client.service";
import { GhgService } from "../../../services/ghg/ghg.service";
import { computedFrom } from "aurelia-framework";
import { BaseOfficeTime } from "../../pages/modules/common-steps/base-office-time";
import { RequestAnEnvironmentalVerification } from "../../pages/modules/ghg/pages/request-an-environmental-verification/request-an-environmental-verification";
import { ManageVesselService } from "../../../services/vessels/manage-vessel.service";
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";
import { UserClaimsService } from "@rightship-tech/common-ui/services/basic/user-claims.service";

//Load styles
require("../../../styles/ghg-verification-client/core-ghg-verification-client.scss");

@autoinject()
@transient()
export class GhgVerificationClient extends BaseOfficeTime {

    private _subscriptions: Subscription[] = [];

    public existingGhg: number;
    public ghgSubmitted: boolean;
    public ghgAlreadyExists: boolean;
    public vessel: AnonVesselDetailModel;
    public item: GhgVerificationModel;
    public documentClick: EventListenerOrEventListenerObject
    public correspondenceEmails: GhgVerificationCorrespondenceEmail[] = [];
    public showDatePicker: boolean = false;

    @computedFrom('vessel')
    public get vesselName(): string {
        return this.vessel ? this.vessel.name : null;
    }

    public externalGhgVerification: ExternalGhgVerificationModel;

    constructor(private eventAggregator: EventAggregator,
        private ghgService: GhgService,
        private notificationService: NotificationClientService,
        private vesselService: ManageVesselService,
        public requestAnEnvironmentalVerification: RequestAnEnvironmentalVerification,
        private userClientService: UserClientService,
        private userClaimsService: UserClaimsService) {
        super();

        this.documentClick = e => {
            if (!(<any>e).propagationStopped) {
                this.eventAggregator.publish(Messages.documentClick, { source: "document" });
            }
        };

        this._subscriptions.push(eventAggregator.subscribe('show-date-picker', () => {
            this.showDatePicker = true;
        }));

        this._subscriptions.push(eventAggregator.subscribe('hide-date-picker', () => {
            this.showDatePicker = false;
        }));
    }

    async activate() {
        let guid = "";
        const params = window.location.pathname.split("/").filter(x => x != null && x != "");
        if(params.length > 1)
        {
            guid = params[1];
        }

        try {
            const res = await this.userClientService.getMe()
            this.userClaimsService.setUserClaims(res.allowedClaims);
        } 
        catch (e) {
            console.error(e);
        }
        
         this.item = await this.ghgService.getGhgVerificationByGuid(guid);
         const vesselResponse = await this.vesselService.getVesselAnonymously(
            {
                vesselId: this.item.vesselId,
                imo:null,
                bKey:null
            });
         this.vessel = vesselResponse.vessel;
         this.externalGhgVerification = {
             ghgVerification: this.item,
             navigateBack: null,
             skipBasicTab: false,
             submit: this.submitClientEnvironmentalVerification,
             vessel: this.vessel
         };
         this.ghgSubmitted = this.item.currentGhgVerificationStatusHistory.ghgVerificationStatusId != GhgVerificationStatusEnum.PendingShipManager;
         if (!this.ghgSubmitted) {
             this.correspondenceEmails = await this.ghgService.getGhgCorrespondenceEmails(this.item.ghgVerificationId);
         }
    }

    attached() {
        document.addEventListener("click", this.documentClick);
    }

    detached() {
        document.removeEventListener("click", this.documentClick);
        this._subscriptions.forEach(x => {
            x.dispose();
        });
    }

    public submitClientEnvironmentalVerification = () => {
        return new Promise<SaveGhgVerificationResponse>((resolve, reject) => {

            this.requestAnEnvironmentalVerification.validateDataPoints(true).then(() => {

                const changes = this.requestAnEnvironmentalVerification.getChanges();

                this.ghgService.saveClientGhgVerification({
                    changes: changes,
                    guid: this.item.guid,
                    correspondenceEmails: this.correspondenceEmails,
                    evdiFiles: this.requestAnEnvironmentalVerification.evdiFiles,
                    capacityPlanFiles: this.requestAnEnvironmentalVerification.capacityPlanFiles,
                    currentClassCertificateFiles: this.requestAnEnvironmentalVerification.currentClassCertificateFiles,
                    mainEngineShopTestFiles: this.requestAnEnvironmentalVerification.mainEngineShopTestFiles,
                    auxiliaryEngineShopTestFiles: this.requestAnEnvironmentalVerification.auxiliaryEngineShopTestFiles,
                    seaTrialReportFiles: this.requestAnEnvironmentalVerification.seaTrialReportFiles,
                    certifiedDocumentsFiles: this.requestAnEnvironmentalVerification.certifiedDocumentsFiles,
                    reliquefactionDocumentationsFiles: this.requestAnEnvironmentalVerification.reliquefactionDocumentationsFiles,
                    energySavingEquipmentFiles: this.requestAnEnvironmentalVerification.energySavingEquipmentFiles,
                    eexiFiles: this.requestAnEnvironmentalVerification.eexiFiles,
                    eeCertificateFiles: this.requestAnEnvironmentalVerification.eeCertificateFiles,
                    ciiFiles: this.requestAnEnvironmentalVerification.ciiFiles,
                    eplShaPoLiFiles: this.requestAnEnvironmentalVerification.eplShaPoLiFiles,
                    marketingData: {}
                }).then(x => {
                    this.ghgAlreadyExists = x.ghgAlreadyExists;
                    if (x && x.generatedId > 0) {
                        this.ghgSubmitted = true;
                        this.notificationService.show("Environmental review successfully submitted", "", NotificationType.Success);
                        this.requestAnEnvironmentalVerification.clear();
                    } else if (x.ghgAlreadyExists) {
                        this.existingGhg = x.existentGhgId;
                        this.notificationService.show("Validation Error", "Environmental Review Request for this vessel already exists in task backlog.", NotificationType.Error);
                    }
                    resolve(x);
                    return;
                },  () => {
                    this.notificationService.show("Error submitting Environmental Review", "", NotificationType.Error);
                    reject();
                    return;
                });
            }).catch(() => {
                reject();
                return;
            });
        });
    }
}

export interface ExternalGhgVerificationModel {
    skipBasicTab: boolean;
    vessel: AnonVesselDetailModel;
    ghgVerification: GhgVerificationModel;
    submit: () => Promise<SaveGhgVerificationResponse>;
    navigateBack: () => void;
}
